import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  BookOpen,
  Building2,
  Cpu,
  FileText,
  FolderTree,
  Home,
  KeyRound,
  type LucideIcon,
  PlayCircle,
  Settings,
  Shield,
  X,
} from "lucide-react";

import { Logo } from "@/components/shared/logo";
import { cn } from "@/lib/utils";
import { ROUTES } from "@/lib/constants";
import { useAuth } from "@/hooks/use-auth";
import { useSidebar } from "@/hooks/use-sidebar";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { isSuperAdmin } from "@/lib/roles";
import { UserMenu } from "./user-menu";
import { WorkspaceSelector } from "./workspace-selector";

interface NavItem {
  labelKey: TranslationKey;
  icon: LucideIcon;
  to: string;
}

interface NavGroup {
  labelKey: TranslationKey;
  items: ReadonlyArray<NavItem>;
}

// Sesi POLISH-NAV-API (v0.29.0) — mirror the desktop sidebar's
// grouped nav so mobile shares the same information architecture.
const NAV_GROUPS: ReadonlyArray<NavGroup> = [
  {
    labelKey: "navigation.groups.main",
    items: [
      { labelKey: "navigation.items.dashboard", icon: Home, to: ROUTES.DASHBOARD },
    ],
  },
  {
    labelKey: "navigation.groups.aiOperations",
    items: [
      { labelKey: "navigation.items.apiKeys", icon: KeyRound, to: ROUTES.API_KEYS },
      { labelKey: "navigation.items.aiProviders", icon: Cpu, to: ROUTES.PROVIDERS },
      {
        labelKey: "navigation.items.playground",
        icon: PlayCircle,
        to: ROUTES.PLAYGROUND,
      },
      {
        labelKey: "navigation.items.observability",
        icon: Activity,
        to: ROUTES.OBSERVABILITY,
      },
    ],
  },
  {
    labelKey: "navigation.groups.governance",
    items: [
      { labelKey: "navigation.items.policies", icon: FileText, to: ROUTES.POLICIES },
      { labelKey: "navigation.items.assets", icon: Shield, to: ROUTES.ASSETS },
      { labelKey: "navigation.items.risk", icon: AlertTriangle, to: ROUTES.RISK },
      {
        labelKey: "navigation.items.compliance",
        icon: BookOpen,
        to: ROUTES.COMPLIANCE,
      },
    ],
  },
  {
    labelKey: "navigation.groups.preferences",
    items: [
      {
        labelKey: "navigation.items.workspaces",
        icon: FolderTree,
        to: ROUTES.WORKSPACES,
      },
      { labelKey: "navigation.items.settings", icon: Settings, to: ROUTES.SETTINGS },
    ],
  },
];

const SUPER_ADMIN_NAV_GROUP: NavGroup = {
  labelKey: "navigation.groups.platform",
  items: [
    {
      labelKey: "navigation.items.platformTenants",
      icon: Building2,
      to: ROUTES.PLATFORM_TENANTS,
    },
  ],
};

export function SidebarMobile() {
  const location = useLocation();
  const { isMobileOpen, closeMobile } = useSidebar();
  const { t } = useTranslation();
  const { user } = useAuth();
  const navGroups = isSuperAdmin(user?.role)
    ? [...NAV_GROUPS, SUPER_ADMIN_NAV_GROUP]
    : NAV_GROUPS;

  // Auto-close on route change.
  useEffect(() => {
    if (isMobileOpen) closeMobile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 transition-opacity duration-200 tablet:hidden"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}
      <aside
        className={cn(
          "fixed left-0 top-0 z-40 flex h-screen w-sidebar-mobile flex-col border-r border-ocean-100 bg-ocean-50 transition-transform duration-250 ease-out tablet:hidden",
          isMobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
        aria-hidden={!isMobileOpen}
      >
        {/* Header */}
        <div className="flex h-14 items-center justify-between border-b border-ocean-100 px-4">
          <Logo size="sm" />
          <button
            type="button"
            onClick={closeMobile}
            className="rounded-md p-1.5 text-neutral-600 transition-colors hover:bg-ocean-100"
            aria-label={t("common.close")}
          >
            <X size={20} />
          </button>
        </div>

        {/* Workspace */}
        <div className="border-b border-ocean-100 px-4 py-3">
          <WorkspaceSelector />
        </div>

        {/* Nav — workflow-grouped, same shape as desktop sidebar. */}
        <nav
          aria-label={t("navigation.sidebar.ariaLabel")}
          className="flex-1 overflow-y-auto px-3 py-4"
        >
          <div className="space-y-5">
            {navGroups.map((group) => (
              <div key={group.labelKey}>
                <h3 className="px-3 pb-1 text-[10px] font-medium uppercase tracking-wide text-ocean-600">
                  {t(group.labelKey)}
                </h3>
                <ul className="space-y-1">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const path = location.pathname;
                    const isActive =
                      path === item.to || path.startsWith(`${item.to}/`);
                    return (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          aria-current={isActive ? "page" : undefined}
                          className={cn(
                            "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors",
                            isActive
                              ? "bg-ocean-100 font-medium text-ocean-900"
                              : "text-neutral-600 active:bg-ocean-100/50"
                          )}
                        >
                          <Icon size={18} className="shrink-0" />
                          <span>{t(item.labelKey)}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </nav>

        {/* User footer */}
        <div className="border-t border-ocean-100 px-3 py-3">
          <UserMenu />
        </div>
      </aside>
    </>
  );
}
